import { lazy, Suspense, useEffect, useState } from 'react'
import { useUIStore } from '@/store'
import { AppShell } from '@/components/layout/AppShell'
import { HelpProvider } from '@/components/help/HelpProvider'
import { useDebts, useLedger } from '@/hooks'
import { AuthProvider } from '@/auth/AuthProvider'
import { AuthCallbackPage } from '@/auth/AuthCallbackPage'
import { useAuth } from '@/auth/useAuth'
import { LandingPage } from '@/landing/LandingPage'
import { SignInGateModule } from '@/components/auth/SignInGateModule'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { isSupabaseConfigured } from '@/repository/supabase/client'
import { softNavigate, useCurrentPathname } from '@/lib/softRouting'
import { PendingInviteHandler } from '@/components/household/PendingInviteHandler'

// Lazy: only invite-link visitors load it.
const JoinPage = lazy(() =>
  import('@/components/household/JoinPage').then((m) => ({ default: m.JoinPage }))
)
import './index.css'

/**
 * Module components are lazy-loaded so recharts + d3-* (~70% of the bundle)
 * only enter the network as the user navigates to a chart-bearing route.
 * Initial paint ships the layout + active module only.
 */
const DashboardView = lazy(() =>
  import('@/components/dashboard/DashboardView').then((m) => ({ default: m.DashboardView }))
)
const DebtTable = lazy(() =>
  import('@/components/registry/DebtTable').then((m) => ({ default: m.DebtTable }))
)
const RecommendationView = lazy(() =>
  import('@/components/engine/RecommendationView').then((m) => ({
    default: m.RecommendationView,
  }))
)
const CashFlowView = lazy(() =>
  import('@/components/cashflow/CashFlowView').then((m) => ({ default: m.CashFlowView }))
)
const PathToFreedomView = lazy(() =>
  import('@/components/journey/PathToFreedomView').then((m) => ({
    default: m.PathToFreedomView,
  }))
)

/**
 * Two-stage reveal mirrors the auth `LoadingPlaceholder` pattern (v0.8.19):
 *
 *   - 0–300ms: invisible. Module chunks already in the SW cache resolve
 *     well inside this window, so navigating between modules doesn't flash
 *     a "Loading…" string on every click.
 *   - 300ms+: spinner + text fade in. Only surfaces when the chunk really
 *     needs to download (first visit, cold network, SW eviction).
 *
 * Pre-v0.8.23 the fallback rendered immediately, which felt janky on every
 * navigation even when the chunk was instant.
 */
const MODULE_FALLBACK_DELAY_MS = 300

function ModuleFallback() {
  const [show, setShow] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setShow(true), MODULE_FALLBACK_DELAY_MS)
    return () => clearTimeout(t)
  }, [])

  return (
    <div
      className="flex items-center justify-center py-16 text-slate-500 text-sm"
      role="status"
      aria-live="polite"
    >
      {show && (
        <div className="flex items-center gap-3">
          <span
            className="inline-block w-4 h-4 border-2 border-slate-700 border-t-emerald-400 rounded-full animate-spin"
            aria-hidden="true"
          />
          <span>Loading module…</span>
        </div>
      )}
    </div>
  )
}

function AppRoutes() {
  // Initialize debts on mount (loads from localStorage / seed data)
  useDebts()
  // Initialize ledger after debts (depends on debts to bootstrap first month)
  useLedger()

  const activeModule = useUIStore((s) => s.activeModule)
  const { isAuthenticated } = useAuth()

  // Demo-mode gates: Strategy Engine + Cash Flow are reserved for
  // signed-in users when this build has Supabase wired up. Local-only
  // builds (no env vars) skip the gate entirely — there's no sign-in
  // path available, so gating would be a dead end. Dashboard + Debt
  // Registry stay open in demo mode so visitors can feel the product
  // without commitment.
  const gateActive = isSupabaseConfigured() && !isAuthenticated
  const engineGated = gateActive && activeModule === 'engine'
  const cashflowGated = gateActive && activeModule === 'cashflow'
  const journeyGated = gateActive && activeModule === 'journey'

  return (
    <AppShell>
      <Suspense fallback={<ModuleFallback />}>
        {activeModule === 'dashboard' && <DashboardView />}
        {activeModule === 'registry' && <DebtTable />}
        {activeModule === 'engine' &&
          (engineGated ? (
            <SignInGateModule
              title="Strategy Engine"
              pitch="The cheapest path to zero, simulated 360 months out against your real numbers."
              bullets={[
                'Avalanche, Snowball, Hybrid, and Minimum-Only ranked by months and dollars',
                'Honest scoring against your real balances, APRs, and active promo windows',
                'See how much you save vs. paying only the lender minimums',
              ]}
            />
          ) : (
            <RecommendationView />
          ))}
        {activeModule === 'cashflow' &&
          (cashflowGated ? (
            <SignInGateModule
              title="Cash Flow"
              pitch="Plan and reconcile every month against income, essentials, and the survival floor."
              bullets={[
                'Plan vs. actual on every debt allocation, every month',
                'Closed months are frozen — your history stays honest',
                'Auto-rolls planning entries to the next month at close-time',
              ]}
            />
          ) : (
            <CashFlowView />
          ))}
        {activeModule === 'journey' &&
          (journeyGated ? (
            <SignInGateModule
              title="Path to Freedom"
              pitch="See the order your debts clear under your chosen strategy — each one a named win with a projected date."
              bullets={[
                'Every debt becomes a milestone with its own clearance date',
                'Debt-free in N months headline — the answer to “how much longer?”',
                'Adjust the strategy or extra payment and watch the dates move in real time',
              ]}
            />
          ) : (
            <PathToFreedomView />
          ))}
      </Suspense>
    </AppShell>
  )
}

/**
 * Cheap, dependency-free routing — four slots only:
 *   - `/auth/callback`             → AuthCallbackPage (magic-link return)
 *   - `/join`                      → JoinPage (household invite, v0.8.51)
 *   - signed in OR has user data OR opted in this session → AppRoutes
 *   - otherwise                    → LandingPage (marketing)
 *
 * When we need more than three routes (settings page, billing, etc.) we'll
 * swap in a real router.
 */
function isAuthCallbackRoute(pathname: string): boolean {
  return pathname.startsWith('/auth/callback')
}

/**
 * `/landing` is the canonical "show me the marketing landing even though I'm
 * signed in" path. Signed-in users get it from the avatar-menu "View landing
 * page" link (typically opened in a new tab). Anonymous users with existing
 * data would normally be routed straight to the app at `/`; deep-linking
 * `/landing` lets them see the marketing surface anyway.
 */
function isLandingRoute(pathname: string): boolean {
  return pathname === '/landing' || pathname === '/landing/'
}

/**
 * Routing predicate replacing the v0.7–v0.8.15 `debtos:enteredApp:v1`
 * persistent flag. The old flag turned the landing into a once-per-browser
 * surface — clicked the demo CTA once, never saw the landing again. Founder
 * feedback (v0.8.16): "the URL should start with landing page... the demo
 * button can go to the demo page." Derive the routing decision from
 * observable state instead:
 *
 *   - User has data they'd return to (any user-data array non-empty, or
 *     recovery anchor present) → straight to AppRoutes
 *   - Otherwise → LandingPage at `/`
 *
 * Why this beats the flag: clearing localStorage naturally resets to
 * landing (correct), there's no flag drift between sign-in/-out events,
 * and the criterion ("did they commit?") matches user intent.
 */
const USER_DATA_KEYS = [
  'debtos:debts:v1',
  'debtos:incomeSources:v1',
  'debtos:ledger:v1',
  'debtos:recurringExpenses:v1',
  'debtos:recoveryAnchor:v1',
] as const

function hasUserData(): boolean {
  if (typeof window === 'undefined') return false
  try {
    for (const key of USER_DATA_KEYS) {
      const raw = localStorage.getItem(key)
      if (!raw) continue
      const parsed = JSON.parse(raw) as unknown
      if (Array.isArray(parsed)) {
        if (parsed.length > 0) return true
      } else if (parsed && typeof parsed === 'object') {
        // Recovery anchor is a singleton object — its presence counts.
        return true
      }
    }
    return false
  } catch {
    return false
  }
}

/**
 * Session-scoped flag: the visitor clicked "Try the demo" this session.
 * Lets them stay in the app across in-tab reloads even if they haven't
 * added data yet. New tab / new session naturally drops back to the
 * landing — exactly what the founder asked for in v0.8.16.
 *
 * Uses sessionStorage (not React state) so a window.location redirect
 * from `/landing` → `/` preserves the signal across the hard navigation.
 */
const ENTERED_SESSION_KEY = 'debtos:enteredAppSession:v1'

function hasEnteredThisSession(): boolean {
  try {
    return typeof window !== 'undefined' && sessionStorage.getItem(ENTERED_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

function markEnteredThisSession(): void {
  try {
    sessionStorage.setItem(ENTERED_SESSION_KEY, '1')
  } catch {
    // Private browsing / quota — non-fatal. Visitor sees the landing on
    // the next route evaluation; click any CTA to re-enter.
  }
}

/**
 * One-time cleanup of the v0.7–v0.8.15 persistent flag. Removes it on
 * every boot so it doesn't sit in user localStorage forever. Idempotent
 * for users who never had it set.
 */
function cleanupLegacyEnteredAppKey(): void {
  try {
    if (typeof window !== 'undefined') localStorage.removeItem('debtos:enteredApp:v1')
  } catch {
    // Storage unavailable — non-fatal, just leaves a stale key.
  }
}

/**
 * Soft loading state shown while `useAuth().isLoading` is true. Replaces
 * the pre-v0.8.18 silent `bg-slate-950` div which looked indistinguishable
 * from a crash when hydration took longer than expected.
 *
 * **Two-stage reveal** to keep fast hydrations from flashing visible UI
 * on every hard navigation:
 *
 *   - 0–300ms: invisible (just the bg-slate-950 frame, same as pre-v0.8.18).
 *     The common case is getSession resolves in well under this on a
 *     warm cache, so the user sees nothing — same as before.
 *   - 300ms–5s: spinner + "Loading your session…" text fade in. Only
 *     surfaces when hydration is actually slow.
 *   - 5s+: "Continue without account" escape hatch appears so a hung
 *     auth path can never strand the user on the placeholder.
 *
 * v0.8.18 shipped without the 300ms defer and the spinner flashed on
 * every hard navigation (logo → /landing → / pattern) because each one
 * does a full page reload, remounting AuthProvider with isLoading=true
 * for the few hundred ms until getSession settles. v0.8.19 fix.
 */
const SPINNER_DELAY_MS = 300
const ESCAPE_HATCH_DELAY_MS = 5000

function LoadingPlaceholder() {
  const [showSpinner, setShowSpinner] = useState(false)
  const [showEscape, setShowEscape] = useState(false)
  useEffect(() => {
    const spinnerTimer = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS)
    const escapeTimer = setTimeout(() => setShowEscape(true), ESCAPE_HATCH_DELAY_MS)
    return () => {
      clearTimeout(spinnerTimer)
      clearTimeout(escapeTimer)
    }
  }, [])

  return (
    <div
      className="min-h-dvh bg-slate-950 flex flex-col items-center justify-center px-4 text-center"
      role="status"
      aria-live="polite"
    >
      {showSpinner && (
        <div className="flex items-center gap-3 text-slate-400 text-sm">
          <span
            className="inline-block w-4 h-4 border-2 border-slate-700 border-t-emerald-400 rounded-full animate-spin"
            aria-hidden="true"
          />
          <span>Loading your session…</span>
        </div>
      )}
      {showEscape && (
        <div className="mt-6 max-w-sm text-xs text-slate-500 leading-relaxed">
          <p>Taking longer than usual. Network slow or session refresh hung?</p>
          <button
            onClick={() => {
              try {
                sessionStorage.setItem('debtos:enteredAppSession:v1', '1')
              } catch {
                /* private browsing — escape hatch may not survive reload */
              }
              if (typeof window !== 'undefined') window.location.assign('/')
            }}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-medium transition-colors"
          >
            Continue without account
          </button>
        </div>
      )}
    </div>
  )
}

function RootRouter() {
  const { isAuthenticated, isLoading } = useAuth()
  // v0.8.24: subscribe to pathname changes so `softNavigate('/landing')`
  // from the Header logo (or `softNavigate('/')` from Landing's
  // "Continue without account") re-renders this component without a hard
  // reload. Pre-fix every transition was `window.location.assign` which
  // bounced through AuthProvider hydration + module re-imports and was
  // the load-bearing source of the "loading is choppy" report.
  const pathname = useCurrentPathname()
  // Within-session "I opted into the demo" signal — bumps from sessionStorage
  // on mount and is flipped by the landing CTA. Outside this session the
  // routing falls back to `hasUserData()` alone.
  const [enteredSession, setEnteredSession] = useState<boolean>(() => {
    cleanupLegacyEnteredAppKey()
    return hasEnteredThisSession()
  })

  // While the auth provider is hydrating from a persisted session, render
  // a soft loading state rather than flashing the landing — a returning
  // signed-in user shouldn't see "Sign in" for half a second. v0.8.18:
  // the placeholder now has a visible spinner + after ~5s an escape
  // hatch so a slow hydration doesn't look like a crash to the user.
  // (The 6s getSession timeout in AuthProvider should usually unblock
  // before this surfaces; the escape hatch is the belt-and-suspenders
  // path for any case where loading is somehow still true past 5s.)
  if (isLoading) {
    return <LoadingPlaceholder />
  }

  // Explicit /landing route — surfaces the marketing landing for signed-in
  // visitors (preview-via-avatar-menu) and anonymous deep-linkers. The
  // "Continue without account" CTA enters the app via softNavigate('/')
  // — no hard reload, no second AuthProvider hydration.
  if (isLandingRoute(pathname)) {
    return (
      <LandingPage
        onContinueWithoutAccount={() => {
          markEnteredThisSession()
          setEnteredSession(true)
          softNavigate('/')
        }}
      />
    )
  }

  // Anonymous visitor at `/` with neither data nor a session opt-in → landing.
  if (!isAuthenticated && !hasUserData() && !enteredSession) {
    return (
      <LandingPage
        onContinueWithoutAccount={() => {
          markEnteredThisSession()
          setEnteredSession(true)
        }}
      />
    )
  }

  return (
    <HelpProvider>
      <AppRoutes />
    </HelpProvider>
  )
}

/** v0.8.51 — `/join?token=…` household invite links. */
function isJoinRoute(pathname: string): boolean {
  return pathname === '/join' || pathname === '/join/'
}

function AppRouterShell() {
  const pathname = useCurrentPathname()
  const page = isAuthCallbackRoute(pathname) ? (
    <AuthCallbackPage />
  ) : isJoinRoute(pathname) ? (
    <Suspense fallback={null}>
      <JoinPage />
    </Suspense>
  ) : (
    <RootRouter />
  )
  return (
    <>
      {page}
      {/* Redeems a stored invite token after sign-in, on any route. */}
      <PendingInviteHandler />
    </>
  )
}

export function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppRouterShell />
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
